import { useEffect, useMemo, useState } from 'react'
import { useI18n } from '../../../utils/i18n'
import { fetchCommerceResourcePage } from '../api'
import { errorMessage, formatDate, orderException, text } from './CommerceLogistics.helpers'

// Legacy exception management is an order-derived, read-only view. There is no
// acknowledge/resolve/retry workflow to invent here; audit logs remain separate.
export default function CommerceExceptions() {
  const { lang } = useI18n()
  const zh = lang === 'zh'
  const [records, setRecords] = useState([])
  const [filter, setFilter] = useState('all')
  const [state, setState] = useState('loading')
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const [cursors, setCursors] = useState([''])
  const [nextCursor, setNextCursor] = useState(null)
  const cursor = cursors.at(-1)
  useEffect(() => {
    const controller = new AbortController()
    setState('loading'); setError(''); setRecords([]); setNextCursor(null)
    fetchCommerceResourcePage('orders', { signal: controller.signal, filters: { limit: 50, cursor } })
      .then(page => {
        if (controller.signal.aborted) return
        setRecords(page.records); setNextCursor(page.nextCursor); setState('ready')
      }).catch(error => {
        if (controller.signal.aborted) return
        setError(errorMessage(error, lang)); setState('error')
      })
    return () => controller.abort()
  }, [cursor, attempt, lang])
  const exceptions = useMemo(() => records.map(orderException).filter(row => row.payment || row.logistics), [records])
  const visible = exceptions.filter(row => filter === 'all' || row[filter])
  return <section className="space-y-5">
    <header className="flex items-start justify-between gap-4">
      <div><h2 className="text-xl font-bold">{zh ? '异常' : 'Exceptions'}</h2>
        <p className="mt-1 text-sm text-slate-500">{zh ? '从订单数据筛选支付失败、履约及物流异常；此页不是审计日志。' : 'Payment failures and fulfillment or tracking exceptions from order data, not audit logs.'}</p>
      </div>
      <button type="button" className="rounded border px-3 py-2" disabled={state === 'loading'} onClick={() => { setCursors(['']); setAttempt(value => value + 1) }}>{zh ? '刷新' : 'Refresh'}</button>
    </header>
    {state === 'loading' && <p role="status">{zh ? '正在扫描订单异常…' : 'Scanning order exceptions…'}</p>}
    {state === 'error' && <div role="alert"><p>{error}</p><button type="button" className="underline" onClick={() => setAttempt(value => value + 1)}>{zh ? '重试' : 'Retry'}</button></div>}
    {state === 'ready' && <>
      <div className="flex flex-wrap gap-2" role="group" aria-label={zh ? '异常类型' : 'Exception type'}>
        {['all', 'payment', 'logistics'].map(value => <button key={value} type="button" aria-pressed={filter === value} className={`rounded border px-3 py-2 ${filter === value ? 'bg-amber-100 text-slate-900' : ''}`} onClick={() => setFilter(value)}>
          {({ all: zh ? '全部' : 'All', payment: zh ? '支付' : 'Payment', logistics: zh ? '物流' : 'Logistics' })[value]} ({value === 'all' ? exceptions.length : exceptions.filter(row => row[value]).length})
        </button>)}
      </div>
      <p className="text-xs text-slate-500">{zh ? `当前第 ${cursors.length} 页订单中的异常（每页最多 50 个订单）；数量不是全站总计。` : `Exceptions in order page ${cursors.length} (up to 50 orders); counts are not global totals.`}</p>
      <div className="overflow-x-auto"><table className="w-full text-left text-sm">
        <thead><tr>{(zh ? ['订单', '类型', '详情', '状态', '更新时间'] : ['Order', 'Type', 'Details', 'Status', 'Updated']).map(label => <th key={label} className="px-4 py-3">{label}</th>)}</tr></thead>
        <tbody>{visible.length ? visible.map(({ order, logistics }) => <tr key={order.id} className="border-t">
          <td className="px-4 py-3"><strong>{text(order.number || order.id)}</strong><p className="font-mono text-xs">{text(order.id)}</p></td>
          <td className="px-4 py-3">{logistics ? (zh ? '物流' : 'Logistics') : (zh ? '支付' : 'Payment')}</td>
          <td className="px-4 py-3">{logistics ? text(order.trackingException?.message) || (zh ? '物流状态异常' : 'Logistics status exception') : (zh ? '支付渠道返回失败' : 'Payment provider reported failure')}</td>
          <td className="px-4 py-3">{logistics ? (zh ? '异常' : 'Exception') : (zh ? '失败' : 'Failed')}</td>
          <td className="px-4 py-3">{formatDate(order.updatedAt || order.lastEventAt, lang)}</td>
        </tr>) : <tr><td colSpan={5} className="px-4 py-8 text-center">{zh ? '当前页没有符合筛选条件的异常。' : 'No matching exceptions on this page.'}</td></tr>}</tbody>
      </table></div>
      <nav className="flex gap-3" aria-label={zh ? '订单分页' : 'Order pagination'}>
        <button type="button" disabled={cursors.length === 1} onClick={() => setCursors(values => values.slice(0, -1))}>{zh ? '上一页' : 'Previous'}</button>
        <button type="button" disabled={!nextCursor || cursors.includes(nextCursor)} onClick={() => setCursors(values => [...values, nextCursor])}>{zh ? '下一页' : 'Next'}</button>
      </nav>
    </>}
  </section>
}
